import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { createClient, RtmClient } from 'agora-rtm-react';
import { getRtmAccessToken } from '../../api/agora/auth/getAccessToken';
import {
  MessageTypes,
  TextMessage,
  UpdateAvatarMessage,
  UpdateNameMessage,
  UserType,
  ViewedMessage,
} from '../../pages/types';
import { addMessage, resetUnreadMessages, setAgoraMessageViewed } from '../../redux/chatMessages/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { environment } from 'src/enviroments';
import { createRoom, setLastMessageViewed } from '../../redux/chatRooms/reducer';
import { getUnReadMessages } from '../../api/agora/chatRooms';
import { isPlatform } from '@ionic/react';
import { App } from '@capacitor/app';
import { userSelector } from 'src/redux/user/selector';
import { uid } from 'uid';
import { ChatMessageData, SendNewMessage } from '../../redux/chatMessages/types';
import { addChatNewMessage } from '../../redux/chatMessages/thunk';
import { ChatRoomsData } from '../../redux/chatRooms/types';

type SendSystemMessageParams = {
  roomData?: ChatRoomsData;
  user: Omit<UserType, 'id'> & { id: string };
  specialistId: string;
  message: string;
  locale: string;
  notifyMessage?: string;
};

interface AgoraContextType {
  isLoggedIn: boolean;
  isLoading: boolean;
  loginAgoraClient: () => void;
  agoraClient: RtmClient | null;
  sendSystemMessage: (data: SendSystemMessageParams) => Promise<void>;
  sendAgoraMessage: (message: TextMessage, specialistID: string) => Promise<void>;
}

interface AgoraProviderType {
  children: ReactNode;
}

const AgoraContext = createContext<AgoraContextType>({
  isLoggedIn: false,
  isLoading: false,
} as AgoraContextType);

let agoraClient: RtmClient = createClient(environment.AGORA.appID)();

export const AgoraProvider: FC<AgoraProviderType> = ({ children }): JSX.Element => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currentUser = useSelector(userSelector);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!currentUser?.id) return;

    loginAgoraClient();
  }, [currentUser]);

  const getNewToken = useCallback(async () => {
    if (!currentUser?.id) return;

    const token = await getRtmAccessToken(currentUser.id);
    return token;
  }, [currentUser]);

  const refreshClientToken = async () => {
    if (!agoraClient) return;

    try {
      const token = await getNewToken();
      if (token) {
        await agoraClient.renewToken(token);
      }
    } catch (e) {}
  };

  const loginAgoraClient = useCallback(async () => {
    if (!currentUser?.id) return;

    if (!agoraClient) return;

    if (isLoading) return;

    if (isLoggedIn) return;

    try {
      setIsLoading(true);
      const token = await getNewToken();
      if (token) {
        await agoraClient.login({ uid: currentUser.id, token });
        const dataUnRead = await getUnReadMessages(currentUser.id);
        dispatch(resetUnreadMessages(currentUser.id));
        dataUnRead.forEach((item) => {
          dispatch(
            createRoom({
              room: item.room,
              avatar: item.avatar,
              name: item.name,
              last_message: item.messages
                .filter((m) => m.room_id === item.room.id)
                .sort((a, b) => b.updated_at - a.updated_at)[0],
            })
          );
          item.messages.forEach((message) => {
            dispatch(addMessage(message));
          });
        });
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, agoraClient, isLoading, isLoggedIn]);

  const sendAgoraMessage = useCallback(
    async (message: TextMessage, specialistID: string) => {
      try {
        if (!isLoggedIn) {
          await loginAgoraClient();
        }

        await agoraClient?.sendMessageToPeer({ text: JSON.stringify(message) }, specialistID);
      } catch (e) {}
    },
    [agoraClient, isLoggedIn, loginAgoraClient]
  );

  const sendSystemMessage = useCallback(
    async ({ roomData, user, specialistId, message, locale, notifyMessage }: SendSystemMessageParams) => {
      const message_id = uid(20);
      const updated_at = Date.now();
      const local_room_id = roomData?.room.id ? roomData?.room.id : uid(20);

      const messageInfo: ChatMessageData = {
        id: message_id,
        room_id: local_room_id,
        sender_id: user.id,
        receiver_id: specialistId,
        message,
        updated_at,
        status: 'loading',
        messageType: 'system',
        notifyMessage,
      };

      await dispatch(
        createRoom({
          avatar: roomData?.avatar ? roomData.avatar : '',
          name: roomData?.name ? roomData.name : '',
          room: {
            id: local_room_id,
            client_id: user.id,
            specialist_id: specialistId,
            updated_at,
          },
          last_message: messageInfo,
        })
      );
      await dispatch(addMessage(messageInfo));

      let createdMessageData;
      try {
        createdMessageData = await dispatch(
          addChatNewMessage({
            sender_id: user.id,
            receiver_id: specialistId,
            sender_type: 'client',
            message,
            room_id: local_room_id,
            message_id,
            updated_at,
            messageType: 'system',
            locale,
            ...(notifyMessage && { notifyMessage }),
          } as SendNewMessage)
        ).unwrap();
        await dispatch(createRoom(createdMessageData));
      } catch (e) {}

      const sendDataMessage: TextMessage = {
        type: MessageTypes.MESSAGE,
        ...createdMessageData,
        avatar: Array.isArray(user.avatar) ? user.avatar[user.avatar.length - 1].url : user.avatar,
        name: `${user.display_name} ${user.last_name}`,
      };

      await sendAgoraMessage(sendDataMessage, specialistId);
    },
    []
  );

  const checkAppActive = async (isActive: boolean) => {
    if (!isActive) return;

    !isLoggedIn && (await loginAgoraClient());
  };

  const checkTabFocused = () => {
    if (document.visibilityState === 'visible') {
      if (!isLoggedIn) {
        (async () => {
          await loginAgoraClient();
        })();
      }
    }
  };

  useEffect(() => {
    if (!isPlatform('hybrid')) return;

    App.addListener('appStateChange', ({ isActive }) => {
      checkAppActive(isActive);
    });
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isPlatform('desktop')) return;

    window.addEventListener('focus', checkTabFocused);
    return () => {
      window.removeEventListener('focus', checkTabFocused);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!agoraClient) return;

    agoraClient.on('TokenExpired', async () => {
      setIsLoggedIn(false);
      try {
        await refreshClientToken();
      } catch (e) {
        console.error('Agora:TokenExpired ', e);
      }
    });

    agoraClient.on('MessageFromPeer', (message, peerId) => {
      if (message.messageType === 'TEXT') {
        const dataMessage: TextMessage | UpdateAvatarMessage | ViewedMessage | UpdateNameMessage = JSON.parse(
          message.text
        );

        switch (dataMessage.type) {
          case MessageTypes.MESSAGE:
            const textMessage: TextMessage = { ...dataMessage } as TextMessage;
            dispatch(createRoom(textMessage));
            dispatch(addMessage(textMessage.last_message));
            break;
          case MessageTypes.VIEWED_MESSAGE:
            const viewedMessage: ViewedMessage = {
              ...dataMessage,
            } as ViewedMessage;
            dispatch(setLastMessageViewed(viewedMessage));
            dispatch(setAgoraMessageViewed(viewedMessage));
            break;
        }
      }
    });

    agoraClient.on('ConnectionStateChanged', (newState, reason) => {
      if (newState === 'CONNECTED' && reason === 'LOGIN_SUCCESS') {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }

      if (newState === 'ABORTED' && reason === 'REMOTE_LOGIN') {
        agoraClient.logout();
      }

      console.log('Agora Status Connection: ', newState, reason);
    });
  }, [agoraClient]);

  return (
    <AgoraContext.Provider
      value={{
        isLoggedIn,
        isLoading,
        agoraClient,
        loginAgoraClient,
        sendSystemMessage,
        sendAgoraMessage,
      }}
    >
      {children}
    </AgoraContext.Provider>
  );
};

export default function useAgoraRtm() {
  return useContext(AgoraContext);
}
